import { DateTime } from "luxon";
import { minBy, orderBy } from "lodash";
import { CompletionScheduleInstanceStatus } from "@src/types.generated";

export type Timeslot = {
  id: string;
  start: string;
  end: string;
  lateDeadline?: string | null;
  status: CompletionScheduleInstanceStatus;
};

// setTimeout fires at once for delays past a signed 32-bit int.
export const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const toMillis = (iso: string): number => new Date(iso).getTime();

// The server starts early only the very next timeslot (the earliest one
// still ahead, submitted or not): once that one is submitted early, the ones
// after it can't be started until it has begun. Mirrors mobile's
// TaskDetailByTimeslotScreen.
export const nextUpcomingTimeslot = <T extends Timeslot>(
  slots: T[],
  now: number,
): T | undefined => {
  const submittedEarlyStart = minBy(
    slots
      .filter(
        (s) =>
          s.status !== CompletionScheduleInstanceStatus.Upcoming &&
          toMillis(s.start) > now,
      )
      .map((s) => toMillis(s.start)),
  );
  const upcoming = minBy(
    slots.filter((s) => s.status === CompletionScheduleInstanceStatus.Upcoming),
    (s) => toMillis(s.start),
  );
  return upcoming &&
    (submittedEarlyStart === undefined ||
      toMillis(upcoming.start) < submittedEarlyStart)
    ? upcoming
    : undefined;
};

// Mobile's pickCurrentTimeslot: a past-due slot someone already started,
// then an open slot, then a past-due slot, then the next upcoming one.
export const pickTimeslot = <T extends Timeslot>(
  slots: T[],
  startedAtBySlotId: Record<string, string | null | undefined>,
  now: number,
): T | undefined => {
  const pastDue = orderBy(
    slots.filter((s) => s.status === CompletionScheduleInstanceStatus.PastDue),
    (s) => toMillis(s.end),
    "desc",
  );
  const open = orderBy(
    slots.filter((s) => s.status === CompletionScheduleInstanceStatus.Open),
    (s) => toMillis(s.end),
  );
  return (
    pastDue.find((s) => !!startedAtBySlotId[s.id]) ??
    open[0] ??
    pastDue[0] ??
    nextUpcomingTimeslot(slots, now)
  );
};

// Slots whose drafts decide the pick: every open and past-due one, and the
// next upcoming one.
export const draftCandidateSlots = <T extends Timeslot>(
  slots: T[],
  now: number,
): T[] => {
  const next = nextUpcomingTimeslot(slots, now);
  return [
    ...slots.filter(
      (s) =>
        s.status === CompletionScheduleInstanceStatus.Open ||
        s.status === CompletionScheduleInstanceStatus.PastDue,
    ),
    ...(next ? [next] : []),
  ];
};

// When a slot stops accepting submissions: its late deadline while late
// submissions are on, otherwise the window end (scadmin slot_closes_at).
export const slotClosesAt = (
  slot: Pick<Timeslot, "end" | "lateDeadline">,
  lateSubmissions: boolean,
): number =>
  lateSubmissions && slot.lateDeadline
    ? Math.max(toMillis(slot.lateDeadline), toMillis(slot.end))
    : toMillis(slot.end);

export type SlotPhase = "upcoming" | "open" | "pastDue" | "closed";

export const slotPhase = (
  slot: Pick<Timeslot, "start" | "end" | "lateDeadline">,
  lateSubmissions: boolean,
  now: number,
): SlotPhase => {
  if (now < toMillis(slot.start)) return "upcoming";
  if (now <= toMillis(slot.end)) return "open";
  if (now <= slotClosesAt(slot, lateSubmissions)) return "pastDue";
  return "closed";
};

// The next moment slotPhase or a minute countdown can change.
export const nextSlotTick = (
  slot: Pick<Timeslot, "start" | "end" | "lateDeadline">,
  lateSubmissions: boolean,
  now: number,
): number | undefined => {
  const phase = slotPhase(slot, lateSubmissions, now);
  if (phase === "closed") return undefined;
  if (phase === "upcoming") return toMillis(slot.start);
  if (phase === "open") return toMillis(slot.end) + 1;
  const closes = slotClosesAt(slot, lateSubmissions);
  return Math.min(closes + 1, now + ((closes - now) % 60_000 || 60_000));
};

export const formatMinutes = (minutes: number): string =>
  minutes < 60
    ? `${minutes} min`
    : `${Math.floor(minutes / 60)}h ${minutes % 60} min`;

export const minutesUntil = (at: number, now: number): number =>
  Math.max(1, Math.ceil((at - now) / 60_000));

export const formatSlotTime = (at: string | number, zone: string): string =>
  (typeof at === "number" ? DateTime.fromMillis(at) : DateTime.fromISO(at))
    .setZone(zone)
    .toFormat("h:mm a");

// "Today", "Tomorrow", "Yesterday" or "Wed, Nov 5", in the store's zone.
export const formatSlotDay = (
  at: string | number,
  zone: string,
  now: number,
): string => {
  const date = (
    typeof at === "number" ? DateTime.fromMillis(at) : DateTime.fromISO(at)
  ).setZone(zone);
  const today = DateTime.fromMillis(now).setZone(zone);
  if (date.hasSame(today, "day")) return "Today";
  if (date.hasSame(today.plus({ days: 1 }), "day")) return "Tomorrow";
  if (date.hasSame(today.minus({ days: 1 }), "day")) return "Yesterday";
  return date.toFormat("EEE, MMM d");
};

export const formatSlotRange = (
  slot: Pick<Timeslot, "start" | "end">,
  zone: string,
  now: number,
): string =>
  `${formatSlotDay(slot.start, zone, now)} · ${formatSlotTime(
    slot.start,
    zone,
  )} – ${formatSlotTime(slot.end, zone)}`;

export const formatSubmissionStartsNotice = (
  start: string,
  zone: string,
  now: number,
): string => {
  const date = DateTime.fromISO(start).setZone(zone);
  const time = formatSlotTime(start, zone);
  return date.hasSame(DateTime.fromMillis(now).setZone(zone), "day")
    ? `Submission for this checklist starts at ${time}`
    : `Submission for this checklist starts on ${date.toFormat(
        "MMM d",
      )} at ${time}`;
};
